import { useState } from 'react'
import { Modal } from './Modal'
import { Field } from './primitives/Field'
import { Notice } from './primitives/Notice'
import { dateInputToIso, isoToDateInput } from '../api/expiry'

// Expiry control (ATC-131 / ATC-121).
//
// TWO ACTIONS ON THE WIRE, not one nullable field: `{ expiresAt }` sets,
// `{ clearExpiry: true }` clears. `{ expiresAt: null }` is not the clear signal
// and is never sent — the server recognises the key but tests its VALUE, so a
// null falls through to `patch_action_missing`.
//
// The SELECTOR IS OMITTED, so the change applies to EVERY credential. That
// follows the status precedent rather than rotation's: an expiry is a
// commercial term on the environment, and leaving the atc_fd_ credential alive
// past the customer's term is the failure the all-rows default prevents.
export function SetExpiryModal({
  customerEnvironmentId,
  currentExpiresAt,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  customerEnvironmentId: string
  currentExpiresAt: string | null
  busy: boolean
  error: string | null
  /** `null` clears. */
  onConfirm: (expiresAt: string | null) => void
  onCancel: () => void
}) {
  const [dateValue, setDateValue] = useState(isoToDateInput(currentExpiresAt))

  const iso = dateInputToIso(dateValue)
  const invalid = dateValue !== '' && iso === null

  return (
    <Modal title="Set licence expiry" onClose={onCancel}>
      <p>
        Applies to <strong>every credential</strong> on{' '}
        <code className="cei">{customerEnvironmentId}</code>. An expiry is a commercial term on the
        environment, not a property of one credential — leaving the Flight Deck credential alive
        past the customer's term is what this prevents.
      </p>

      <Field
        label="Expires on"
        hint="End of the chosen day, UTC — a licence bought “until the 31st” works on the 31st."
        error={invalid ? 'Not a valid date.' : undefined}
      >
        <input
          type="date"
          value={dateValue}
          onChange={(e) => setDateValue(e.target.value)}
          disabled={busy}
        />
      </Field>

      <div className="notice">
        Expiry is a <strong>second axis from status</strong>. An expired environment still reports
        a green <code>active</code> on every list — the credential is refused at validation with{' '}
        <code>token_expired</code>. A past date takes effect immediately and is reversible: set a
        future date, or clear it.
      </div>

      {error && <Notice tone="error">{error}</Notice>}

      <div className="modal-actions">
        <button onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        {currentExpiresAt && (
          <button onClick={() => onConfirm(null)} disabled={busy}>
            {busy ? 'Working…' : 'Clear expiry'}
          </button>
        )}
        <button className="btn-primary" disabled={busy || iso === null} onClick={() => onConfirm(iso)}>
          {busy ? 'Saving…' : 'Set expiry'}
        </button>
      </div>
    </Modal>
  )
}
